import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
  Skeleton
} from '@mui/material';
import { formatDistance, parseISO, addYears, isFuture } from 'date-fns';

const ServiceCard = ({ 
  title, 
  lastServiceDate,
  historicalData = [],
  mileageInterval,
  yearInterval,
  description 
}) => {
  if (!title) {
    return null;
  }

  // Show loading state if dates are being fetched
  if (lastServiceDate === undefined) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Skeleton variant="rectangular" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={40} />
        </CardContent>
      </Card>
    );
  }

  // No service history card
  if (!lastServiceDate) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Typography variant="body1" color="textSecondary">
            No service history recorded
          </Typography>
          {description && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  const today = new Date();
  const serviceDate = parseISO(lastServiceDate);
  
  // If the date is in the future, treat it as a due date
  // If it's in the past, treat it as a service date and calculate the due date
  const dueDate = isFuture(serviceDate) ? serviceDate : addYears(serviceDate, yearInterval);
  
  const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  const daysTotal = yearInterval * 365;
  const isOverdue = daysRemaining <= 0;
  const progress = Math.min(100, ((daysTotal - Math.max(0, daysRemaining)) / daysTotal) * 100);

  // Calculate mileage-based status if we have the data
  let mileageInfo = null;
  if (mileageInterval && historicalData?.length >= 2) {
    const serviceIndex = historicalData.findIndex(d => 
      d.date === lastServiceDate
    );
    
    if (serviceIndex !== -1) {
      const serviceMileage = historicalData[serviceIndex].current_odometer;
      const currentMileage = historicalData[0].current_odometer;
      const milesDriven = currentMileage - serviceMileage;
      
      // Always use 5,000 miles for Oil Change service type, regardless of what's passed in
      const actualMileageInterval = title === "Oil Change" ? 5000 : mileageInterval;
      const milesRemaining = actualMileageInterval - milesDriven;
      
      mileageInfo = {
        milesDriven,
        milesRemaining: Math.max(0, milesRemaining),
        isOverdue: milesRemaining <= 0,
        progress: (milesDriven / actualMileageInterval) * 100
      };
    }
  }

  // Use mileage status if it's more urgent than time-based status
  const finalStatus = mileageInfo && mileageInfo.progress > progress
    ? {
        isOverdue: mileageInfo.isOverdue,
        progress: mileageInfo.progress,
        message: mileageInfo.isOverdue
          ? `Overdue by ${Math.abs(mileageInfo.milesRemaining).toLocaleString()} miles`
          : `${mileageInfo.milesRemaining.toLocaleString()} miles remaining`
      }
    : {
        isOverdue,
        progress,
        message: isOverdue
          ? `Due ${formatDistance(dueDate, today)} ago`
          : `Due in ${formatDistance(today, dueDate)}`
      };

  return (
    <Tooltip 
      title={`${mileageInfo ? `Mileage: ${mileageInfo.milesDriven.toLocaleString()} of ${title === "Oil Change" ? "5,000" : mileageInterval.toLocaleString()} miles\n` : ''}Due Date: ${dueDate.toLocaleDateString()}`}
      arrow
    >
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          
          <Typography variant="h5" color={finalStatus.isOverdue ? "error" : "inherit"}>
            {finalStatus.isOverdue ? "OVERDUE" : "Service Status"}
          </Typography>
          
          <Typography 
            variant="body1" 
            color={finalStatus.isOverdue ? "error" : "textSecondary"}
          >
            {finalStatus.message}
          </Typography>

          {description && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
              {description}
            </Typography>
          )}

          <Box sx={{ mt: 1.5, position: 'relative', minHeight: 8 }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, finalStatus.progress)}
              color={finalStatus.isOverdue ? "error" : finalStatus.progress > 75 ? "warning" : "primary"}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            {/* Enforce the correct interval for Oil Change service type */}
            {title === "Oil Change" 
              ? `Service required every 5,000 miles or 5 months`
              : mileageInterval
                ? `Service required every ${mileageInterval.toLocaleString()} miles or ${yearInterval < 1 ? Math.round(yearInterval * 12) + ' months' : yearInterval + ' ' + (yearInterval === 1 ? 'year' : 'years')}`
                : `Service required every ${yearInterval < 1 ? Math.round(yearInterval * 12) + ' months' : yearInterval + ' ' + (yearInterval === 1 ? 'year' : 'years')}`
            }
          </Typography>
        </CardContent>
      </Card>
    </Tooltip>
  );
};

export default ServiceCard;